/**
 * lightning — occasional sharp sky flashes for storm scenes. Each of `bolts`
 * independent strike anchors gets a broad sky flash (a big soft glow that
 * back-lights the parallax silhouettes) plus a thin bright bolt streak; both are
 * driven by the pure double-flash envelope (lightningConfig.strikeAlpha) on the
 * anchor's own randomised interval, so strikes are sparse and desynced. Sits in
 * the ambient band (in FRONT of parallax, behind entities) so the whole backdrop
 * lights up. A sky fixture (mooded:false).
 *
 * ACCESSIBILITY: lightning is a strobe → when reduce-motion is on we place the
 * sprites dark and attach NO ticker (a photosensitivity guard), so the scene
 * stays calm. The pref is read ONCE at build time.
 */
import { registerEffect, makeContentHandle, type EffectBuilder } from '../effectBuilders';
import { strikeAlpha, strikeIntervalMs } from '../../vfx/lightningConfig';
import { createGlowSprite } from '../../vfx/glowSpriteFactory';
import { vfxBudgetManager } from '../../VFXBudgetManager';
import { getMotionPrefs } from '../../../systems/accessibility/motionPrefs';
import { seededRand } from '../atmospherePlacement';
import { Z_LAYERS } from '../../../rendering/Z_LAYERS';
import { lerp } from '../../../../shared/utils/MathUtils';
import type { ISprite } from '../../../rendering/IRenderer';

const buildLightning: EffectBuilder = (layer, ctx) => {
  if (layer.kind !== 'lightning') return null;
  const { region } = ctx;
  const spanX = region.x1 - region.x0;
  const spanY = region.y1 - region.y0;
  const reduceMotion = getMotionPrefs().reduceMotion;

  const sprites: ISprite[] = [];
  // Per-anchor: the broad flash sprite, the thin bolt sprite, its strike interval + seed.
  const anchors: { flash: ISprite; bolt: ISprite; intervalMs: number; seed: number }[] = [];

  for (let i = 0; i < layer.bolts; i += 1) {
    if (!vfxBudgetManager.canSpawnShape()) break;
    const seed = ctx.seed + 4200 + i * 31.7;
    const x = lerp(region.x0 + spanX * 0.1, region.x1 - spanX * 0.1, seededRand(seed));
    const skyY = region.y0 + spanY * lerp(0.06, 0.22, seededRand(seed + 1));

    // Broad sky flash — a big soft additive glow high in the sky, starts dark.
    const flash = createGlowSprite({ x, y: skyY, size: Math.max(spanX, spanY) * 0.5, color: layer.color, alpha: 0, blendMode: 'add', parent: ctx.layerContainer });
    if (!flash) break; // textures not ready — quieter sky, never a crash
    // Thin bright bolt streak — a tall ray_ribbon hanging from the sky, slight tilt.
    const bolt = createGlowSprite({ x, y: skyY + spanY * 0.22, size: 8, color: layer.color, alpha: 0, textureKey: 'ray_ribbon', blendMode: 'add', parent: ctx.layerContainer });
    if (!bolt) { flash.destroy(); break; }
    bolt.setDisplaySize(lerp(5, 11, seededRand(seed + 2)), spanY * lerp(0.35, 0.6, seededRand(seed + 3)));
    bolt.setAngle(lerp(-14, 14, seededRand(seed + 4)));

    sprites.push(flash, bolt);
    anchors.push({ flash, bolt, intervalMs: strikeIntervalMs(seed + 5, layer.intervalMinMs, layer.intervalMaxMs), seed });
  }

  if (!sprites.length) return null;
  // Reduce-motion: leave the sprites dark, attach NO ticker (no strobe).
  if (reduceMotion) return makeContentHandle({ sprites });

  let elapsedMs = 0;
  const unsub = ctx.addTick((dtMs: number) => {
    elapsedMs += dtMs;
    for (const a of anchors) {
      const env = strikeAlpha(elapsedMs, a.intervalMs, a.seed);
      a.flash.setAlpha(env * layer.flashAlpha);
      // The bolt reads sharper than the flash — square the envelope so it only
      // shows at the bright moments, not across the whole soft decay.
      a.bolt.setAlpha(env * env * layer.boltAlpha);
    }
  });

  return makeContentHandle({ sprites, onDestroy: unsub });
};

registerEffect({ kind: 'lightning', build: buildLightning, depth: Z_LAYERS.ATMOSPHERE, mooded: false });
